import React, { useCallback, useEffect, useRef, useState } from "react";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import ReceptionCharacterCanvas from "~/components/reception/ReceptionCharacterCanvas";
import { useTranslation } from "react-i18next";
import ReceptionHeader from "~/components/ReceptionHeader";
import "~/css/reception/ReceptionConversationVisitor.css";
import { useInactivityTimeout } from "~/utils/receptionUtils";
import FitText from "~/components/reception/FitText";
import {
  T2SEngineAtom,
  currentSpeakerAtom,
  isAllLoadedAtom,
  languageAtom,
  standbyModeAtom,
  volumeAtom,
} from "~/Atom";
import { talkModes } from "~/Commons";
import { audioManager, playVoice, type PlayVoiceParams } from "~/SoundUtils";
import { api } from "~/api";
import { getCurrentCustomerId } from "~/AtomWithDynamoDB";
import { runReceptionBack } from "~/utils/receptionNavigation";
import { ROUTE } from "~/constants/routes";
import { receptionBowEndExpression } from "~/reception/motion/receptionBowMotion";
import type { DepartmentPicNavigationState } from "~/reception/ReceptionConversationDepartmentPic";

type VoicePromptKey = "initial" | "missingCompany" | "missingVisitor" | "missingBoth";
type VisitorInfo = {
  companyName: string | null;
  visitorName: string | null;
};

const analyzingBarHeights = [26, 21, 21, 26, 34] as const;

const mergeVisitorInfo = (prev: VisitorInfo, next: VisitorInfo): VisitorInfo => ({
  companyName: next.companyName ? next.companyName : prev.companyName,
  visitorName: next.visitorName ? next.visitorName : prev.visitorName,
});

const getNextPromptKey = (hasCompany: boolean, hasVisitor: boolean): VoicePromptKey => {
  if (!hasCompany && hasVisitor) return "missingCompany";
  if (hasCompany && !hasVisitor) return "missingVisitor";
  return "missingBoth";
};

/**
 * 来訪者の会社名・氏名を音声で収集し、必要情報が揃うまで対話を継続する受付画面。
 *
 * - 目的: 音声入力から `companyName` と `visitorName` を抽出し、両方が揃った時点で次画面へ遷移する。
 * - 入力制御: `react-speech-recognition` を利用して、TTS再生後にSTTを開始する。各質問は1回だけ読み上げ、無音でSTTが停止した場合は再読み上げせずに再度リッスンする。
 * - 状態管理: `visitorInfoRef` に抽出途中の情報を保持し、部分的に認識できた情報を次ターンへ引き継ぐ。
 * - UI挙動: 解析中はキャラクター表示を非表示にして進行状態を明示し、待機/解析の切替を明確にする。
 */
const ReceptionConversationVisitor: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleBack = useCallback(() => {
    void SpeechRecognition.stopListening();
    runReceptionBack({ kind: "router-history", navigate });
  }, [navigate]);

  // --- Atoms ---
  const [T2SEngine] = useAtom(T2SEngineAtom);
  const [currentSpeaker] = useAtom(currentSpeakerAtom);
  const [volume] = useAtom(volumeAtom);
  const [language] = useAtom(languageAtom);
  const [isAllLoaded] = useAtom(isAllLoadedAtom);
  const [, setStandbyMode] = useAtom(standbyModeAtom);
  const prompts = React.useMemo<Record<VoicePromptKey, string>>(
    () => ({
      initial: t("receptionConversation.visitor.prompts.initial"),
      missingCompany: t("receptionConversation.visitor.prompts.missingCompany"),
      missingVisitor: t("receptionConversation.visitor.prompts.missingVisitor"),
      missingBoth: t("receptionConversation.visitor.prompts.missingBoth"),
    }),
    [t]
  );

  // --- Local State ---
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // --- Refs (stable, not causing re-render issues) ---
  const askedRef = useRef(false);
  const isThinkingRef = useRef(false);
  const isAwaitingAnswerRef = useRef(false);
  // 解析済み情報を画面遷移まで保持する作業用バッファ
  const visitorInfoRef = useRef<VisitorInfo>({ companyName: null, visitorName: null });

  const { transcript, finalTranscript, listening, resetTranscript } = useSpeechRecognition();

  // --- Setup inactivity timeout ---
  useInactivityTimeout(navigate, transcript);

  useEffect(() => {
    setStandbyMode(talkModes.standby);
  }, [setStandbyMode]);

  useEffect(() => {
    // 画面遷移直後に残存しているSTT状態・文字起こしを初期化する
    resetTranscript();
  }, [resetTranscript]);

  // isTranscribing が true になったら TTS が終わったサイン → STT を開始する
  useEffect(() => {
    if (!isTranscribing) return;
    setIsAnalyzing(true);
    void SpeechRecognition.startListening({ language });
  }, [isTranscribing, language]);

  // --- Core: speak question then listen for answer ---
  const speakAndListen = useCallback(
    async (promptKey: VoicePromptKey) => {
      const params: PlayVoiceParams = {
        inputText: prompts[promptKey],
        T2SEngine,
        currentSpeaker,
        speed: 1.0,
        pitch: 0.0,
        intonation: 1.0,
        volume,
        talkingMode: 9, // チャット履歴に残さない
        setIsTranscribing,
        // 発話終了後にお辞儀モーションを再生する（受付の全画面で統一）。
        endExpression: receptionBowEndExpression(),
      };

      // TTS再生前に文字起こしを無効化・STTを停止する
      setIsTranscribing(false);
      void SpeechRecognition.stopListening();
      await playVoice(params);
      isAwaitingAnswerRef.current = true;
    },
    [T2SEngine, currentSpeaker, prompts, volume]
  );

  // --- Trigger initial question once isAllLoaded becomes true ---
  useEffect(() => {
    // 画面ロード直後の初回質問は一度だけ実行する
    if (!isAllLoaded || askedRef.current) return;
    askedRef.current = true;
    speakAndListen("initial").catch((err) => {
      console.error("[VisitorInfo] speakAndListen failed:", err);
    });
  }, [isAllLoaded, speakAndListen]);

  const extractByAiOrFallback = useCallback(async (recognized: string) => {
    try {
      const customerId = await getCurrentCustomerId();
      const extracted = await api.openai.extractCompanyAndVisitor.query({
        text: recognized,
        customerId,
      });
      return {
        companyName: extracted.company,
        visitorName: extracted.name,
      };
    } catch (error) {
      console.error("[VisitorInfo] AI extraction failed, fallback to regex:", error);
      return {
        companyName: null,
        visitorName: null,
      };
    }
  }, []);

  const handleRecognizedAnswer = useCallback(
    async (recognized: string) => {
      const analyzed = await extractByAiOrFallback(recognized);
      const prevInfo = visitorInfoRef.current;
      const merged = mergeVisitorInfo(prevInfo, analyzed);
      visitorInfoRef.current = merged;

      const hasCompany = Boolean(merged.companyName?.trim());
      const hasVisitor = Boolean(merged.visitorName?.trim());

      isThinkingRef.current = false;

      if (hasCompany && hasVisitor) {
        navigate(ROUTE.RECEPTION.CONVERSATION.DEPARTMENT_PIC, {
          state: {
            companyName: merged.companyName,
            visitorName: merged.visitorName,
          } as DepartmentPicNavigationState,
        });
        return;
      }

      const nextPrompt = getNextPromptKey(hasCompany, hasVisitor);
      setIsAnalyzing(false);
      void speakAndListen(nextPrompt);
    },
    [extractByAiOrFallback, navigate, speakAndListen]
  );

  // --- Process recognized speech when STT finishes ---
  useEffect(() => {
    // TODO: this consolog is check on stg, remove it when release to prod
    console.info("Transcript updated:", transcript, "FinalTranscript:", finalTranscript);
    if (listening || isThinkingRef.current || !isAwaitingAnswerRef.current || !isTranscribing)
      return;

    const recognized = finalTranscript.trim() || transcript.trim();
    if (!recognized) return;

    void SpeechRecognition.stopListening();
    resetTranscript();
    isAwaitingAnswerRef.current = false;
    isThinkingRef.current = true;
    void handleRecognizedAnswer(recognized);
  }, [listening, transcript, finalTranscript, handleRecognizedAnswer, resetTranscript]);

  useEffect(() => {
    if (listening || isThinkingRef.current || !isAwaitingAnswerRef.current || !isTranscribing)
      return;

    const recognized = finalTranscript.trim() || transcript.trim();
    if (recognized) return;

    void SpeechRecognition.startListening({ language });
  }, [listening, transcript, finalTranscript, language]);

  useEffect(() => {
    return () => {
      audioManager.stopCurrentAudio();
      void SpeechRecognition.stopListening();
      resetTranscript();
    };
  }, []);

  const pageShellClassName = isAnalyzing
    ? "reception-conversation-visitor-page__shell reception-conversation-visitor-page__shell--analyzing"
    : "reception-conversation-visitor-page__shell reception-conversation-visitor-page__shell--default";

  return (
    <div className="App-page reception-conversation-page reception-conversation-visitor-page">
      <ReceptionHeader showBackButton onBackButtonClick={handleBack} />

      <main className="reception-conversation-visitor-page__content">
        <section
          className={pageShellClassName}
          aria-labelledby="reception-conversation-visitor-title"
        >
          <div className="reception-conversation-visitor-page__title-group">
            <h1
              id="reception-conversation-visitor-title"
              className="reception-conversation-title reception-conversation-visitor-page__title typo-h1"
            >
              <FitText>{t("receptionConversation.visitor.questionTitle")}</FitText>
            </h1>
          </div>

          {!isAnalyzing && (
            <div className="reception-conversation-visitor-page__character-panel">
              <ReceptionCharacterCanvas canvasStyle={{ height: "100%" }} />
            </div>
          )}

          {isAnalyzing && (
            <div
              className="reception-conversation-visitor-page__analyzing-panel"
              role="status"
              aria-live="polite"
            >
              <div
                className="reception-conversation-visitor-page__analyzing-wave"
                aria-hidden="true"
              >
                {analyzingBarHeights.map((barHeight, index) => (
                  <span
                    key={`left-${index}`}
                    className="reception-conversation-visitor-page__analyzing-bar"
                    style={{ height: `${barHeight}px` }}
                  />
                ))}
              </div>

              <p className="reception-conversation-analyzing reception-conversation-visitor-page__analyzing-text typo-h2">
                {t("receptionConversation.analyzing")}
              </p>

              <div
                className="reception-conversation-visitor-page__analyzing-wave reception-conversation-visitor-page__analyzing-wave--delayed"
                aria-hidden="true"
              >
                {analyzingBarHeights.map((barHeight, index) => (
                  <span
                    key={`right-${index}`}
                    className="reception-conversation-visitor-page__analyzing-bar"
                    style={{ height: `${barHeight}px` }}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default ReceptionConversationVisitor;
